import axios from "axios";

const BASE = `${import.meta.env.VITE_BACKEND_URL.replace(/\/$/, "")}/api`;

const api = axios.create({ baseURL: BASE });

// Injected by ApiTokenBridge in App.jsx — returns the current Clerk JWT for the active org.
let _getToken = null;
export function setTokenGetter(fn) {
  _getToken = fn;
}

// Injected by SiteProvider — returns the active site id, sent as X-Site-Id so RLS scopes to it.
let _getSiteId = null;
export function setSiteIdGetter(fn) {
  _getSiteId = fn;
}

// Attach a fresh Clerk Bearer token to every request so RLS sees the correct org_id.
api.interceptors.request.use(async (config) => {
  const siteId = _getSiteId?.();
  if (siteId) config.headers["X-Site-Id"] = siteId;
  if (_getToken) {
    const token = await _getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("[AUTH] getToken() returned null — no Bearer token attached", { url: config.url });
    }
  } else {
    console.warn("[AUTH] _getToken not set — ApiTokenBridge not mounted yet", { url: config.url });
  }
  return config;
});

// Same auth headers as the axios interceptor, but as a plain object for non-axios
// callers (fetch-based clients like @ai-sdk/react bypass the interceptor).
export async function getAuthHeaders() {
  const headers = {};
  if (_getToken) {
    const token = await _getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const siteId = _getSiteId?.();
  if (siteId) headers["X-Site-Id"] = siteId;
  return headers;
}

// Response interceptor for error logging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    console.error(`[API] ${error.config.method.toUpperCase()} ${error.config.url} - ${status} Error`, {
      status,
      data,
      config: {
        baseURL: error.config.baseURL,
        url: error.config.url,
        headers: error.config.headers,
      },
    });
    return Promise.reject(error);
  }
);

export const getSettings = () => api.get("/settings").then(r => r.data);
export const saveSettings = (data) => api.post("/settings", data).then(r => r.data);
// CMS connection (one per org) — secrets are write-only, GET never returns them.
export const getCmsConnection = () => api.get("/cms/connection").then(r => r.data);
export const saveCmsConnection = (data) => api.put("/cms/connection", data).then(r => r.data);
export const testCmsConnection = (data = {}) => api.post("/cms/connection/test", data).then(r => r.data);
export const deleteCmsConnection = () => api.delete("/cms/connection").then(r => r.data);
export const webflowOauthAuthorize = (returnUrl) =>
  api.get("/cms/webflow/oauth/authorize", { params: { return_url: returnUrl } }).then(r => r.data);
export const listWebflowCollections = (apiToken) => api.post("/cms/webflow/collections", { api_token: apiToken || null }).then(r => r.data);
// Integration keys — list never returns secrets; create returns the raw key once.
export const listIntegrationKeys = () => api.get("/settings/integration-keys").then(r => r.data);
export const createIntegrationKey = (data) => api.post("/settings/integration-keys", data).then(r => r.data);
export const revokeIntegrationKey = (id) => api.delete(`/settings/integration-keys/${id}`).then(r => r.data);
export const draftBusinessContext = (siteUrl = "") => api.post("/settings/business/draft", { site_url: siteUrl }).then(r => r.data);

// Onboarding — token passed explicitly: right after setActive the bridge may still hold the pending session.
export const setWorkspaceType = (accountType, token) =>
  api.post("/onboarding/workspace", { account_type: accountType }, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
export const getOnboarding = () => api.get("/onboarding").then(r => r.data);
export const dismissOnboarding = () => api.post("/onboarding/dismiss").then(r => r.data);

// Sites
export const getSites = () => api.get("/sites").then(r => r.data);
export const addSite = (data) => api.post("/sites", data).then(r => r.data);

export const getOverview = () => api.get("/overview").then(r => r.data);

// Founder-only system alerts — /admin/alerts 404s for everyone not in PLATFORM_ADMIN_USER_IDS.
export const getAdminMe = () => api.get("/admin/me").then(r => r.data);
export const getAdminAlerts = () => api.get("/admin/alerts").then(r => r.data);
export const resolveAdminAlert = (id) => api.post(`/admin/alerts/${id}/resolve`).then(r => r.data);
export const getAdminModels = () => api.get("/admin/models").then(r => r.data);
export const switchAdminModels = (data) => api.put("/admin/models", data).then(r => r.data);
export const getAdminUsage = (days = 30) => api.get("/admin/usage", { params: { days } }).then(r => r.data);
export const putAdminBudget = (data) => api.put("/admin/budgets", data).then(r => r.data);

export const startSync = () => api.post("/sync").then(r => r.data);
export const startFullSync = () => api.post("/sync?force_full=true").then(r => r.data);
export const startRecluster = () => api.post("/sync/recluster").then(r => r.data);
export const startRescore = () => api.post("/sync/rescore").then(r => r.data);
export const startRelabel = () => api.post("/sync/relabel").then(r => r.data);
export const resetSync = () => api.post("/sync/reset").then(r => r.data);
export const getSyncStatus = () => api.get("/sync/status").then(r => r.data);

export const getArticles = (params = {}) => api.get("/articles", { params }).then(r => r.data);
export const getArticle = (id) => api.get(`/articles/${id}`).then(r => r.data);
export const getArticleRawContent = (id) => api.get(`/articles/${id}/raw-content`).then(r => r.data);
export const updateArticleContent = (id, contentHtml) => api.put(`/articles/${id}/content`, { content_html: contentHtml }).then(r => r.data);
export const updateArticleMeta = (id, payload) => api.put(`/articles/${id}/meta`, payload).then(r => r.data);
export const refreshArticleFromWp = (id) => api.post(`/articles/${id}/refresh`).then(r => r.data);
export const updateArticleStatus = (id, status) => api.put(`/articles/${id}/status`, { status }).then(r => r.data);
export const extractArticleEntities = (id) => api.post(`/articles/${id}/extract-entities`).then(r => r.data);
export const getArticleSchemaJsonLd = (id) => api.get(`/articles/${id}/schema-jsonld`).then(r => r.data);
export const scorePersonaFit = (id) => api.post(`/articles/${id}/score-persona-fit`).then(r => r.data);
export const scoreArticleIntelligence = (id, payload = {}) => api.post(`/articles/${id}/score-intelligence`, payload).then(r => r.data);
export const rewriteArticle = (id, payload = {}) => api.post(`/articles/${id}/rewrite`, payload).then(r => r.data);
export const getArticleOutline = (id) => api.get(`/articles/${id}/outline`).then(r => r.data);
export const getArticleOutlineGaps = (id, sections) => api.post(`/articles/${id}/outline-gaps`, { sections }).then(r => r.data);
export const applyArticleRewrite = (id, payload) => api.post(`/articles/${id}/apply-rewrite`, payload).then(r => r.data);

export const getClusters = () => api.get("/clusters").then(r => r.data);
export const getCluster = (id) => api.get(`/clusters/${id}`).then(r => r.data);

export const getCannibalization = (threshold = 0.85) =>
  api.get("/cannibalization", { params: { threshold } }).then(r => r.data);

export const getGraph = () => api.get("/graph").then(r => r.data);
export const getSearchDemandGraph = () => api.get("/graph/search-demand").then(r => r.data);
export const getSearchDemandInsight = (nodeId, regenerate = false) =>
  api.post("/graph/search-demand/insight", { node_id: nodeId, regenerate }).then(r => r.data);
export const createTaskFromInsight = (nodeId) => api.post("/graph/search-demand/insight/task", { node_id: nodeId }).then(r => r.data);

export const getGapAnalysis = () => api.get("/gap-analysis").then(r => r.data);

export const getRecommendations = () => api.get("/recommendations").then(r => r.data);
export const generateRecommendations = (params = {}) => api.post("/recommendations/generate", params).then(r => r.data);
// peek: only read a stored plan ({missing: true} if none); otherwise a missing plan is generated.
export const getTopicPlan = (topicId, { regenerate = false, peek = false } = {}) => api.post("/recommendations/topic-plan", { topic_id: topicId, regenerate, peek }).then(r => r.data);
export const getLinkingSuggestions = () => api.get("/linking-suggestions").then(r => r.data);
export const getLinkProposals = (max = 3) => api.get("/linking/proposals", { params: { max_links: max } }).then(r => r.data);
export const getArticleProposal = (id, max = 3) => api.get(`/linking/proposals/${id}`, { params: { max_links: max } }).then(r => r.data);
export const applyLinks = (data) => api.post("/linking/apply", data).then(r => r.data);
export const applyAllLinks = (items, max = 3) => api.post("/linking/apply-all", { items, max_links_per_article: max }).then(r => r.data);
export const getApplyAllStatus = () => api.get("/linking/apply-all/status").then(r => r.data);
export const getSyncSchedule = () => api.get("/sync/schedule").then(r => r.data);

// Google Search Console
export const getGscSettings = () => api.get("/gsc/settings").then(r => r.data);
export const saveGscSettings = (data) => api.post("/gsc/settings", data).then(r => r.data);
export const testGscConnection = (data) => api.post("/gsc/test", data).then(r => r.data);
export const deleteGscSettings = () => api.delete("/gsc/settings").then(r => r.data);
export const refreshGsc = (daysBack = 28) => api.post("/gsc/refresh", null, { params: { days_back: daysBack } }).then(r => r.data);
export const getGscRefreshStatus = () => api.get("/gsc/refresh/status").then(r => r.data);
export const getGscOAuthUrl = (frontendUrl) =>
  api.get("/gsc/oauth/authorize", { params: { frontend_url: frontendUrl } }).then(r => r.data);
export const getGscProperties = () => api.get("/gsc/properties").then(r => r.data);
export const getIndexingCandidates = () => api.get("/gsc/indexing/candidates").then(r => r.data);
export const getIndexingQuota = () => api.get("/gsc/indexing/quota").then(r => r.data);
export const getIndexingStatus = () => api.get("/gsc/indexing/status").then(r => r.data);
export const stopIndexing = () => api.post("/gsc/indexing/stop").then(r => r.data);
// items: [{ url, wp_post_id }]
export const submitIndexing = (items) => api.post("/gsc/indexing/submit", { urls: items }).then(r => r.data);
export const submitIndexingOne = (url, wpPostId) =>
  api.post("/gsc/indexing/submit-one", { url, wp_post_id: wpPostId ?? null }).then(r => r.data);
export const inspectUrl = (url) => api.post("/gsc/indexing/inspect", { url }).then(r => r.data);

export const listOutlines = () => api.get("/outlines").then(r => r.data);
export const getOutline = (id) => api.get(`/outlines/${id}`).then(r => r.data);
export const getOutlineStatus = (id) => api.get(`/outlines/${id}/status`).then(r => r.data);
export const generateOutline = (data) => api.post("/outlines/generate", data).then(r => r.data);
export const updateOutline = (id, data) => api.put(`/outlines/${id}`, data).then(r => r.data);
export const deleteOutline = (id) => api.delete(`/outlines/${id}`).then(r => r.data);
export const regenerateOutline = (id) => api.post(`/outlines/${id}/regenerate`).then(r => r.data);
export const revertOutline = (id) => api.post(`/outlines/${id}/revert`).then(r => r.data);
export const pushOutlineToWp = (id) => api.post(`/outlines/${id}/push-to-wp`).then(r => r.data);
export const getOutlineWpStatus = (id) => api.get(`/outlines/${id}/wp-status`).then(r => r.data);
export const updateOutlineWpStatus = (id, status) => api.put(`/outlines/${id}/wp-status`, { status }).then(r => r.data);

export const getSitemapTree = () => api.get("/sitemap/tree").then(r => r.data);
export const crawlSitemap = () => api.post("/sitemap/crawl").then(r => r.data);
export const getSitemapPlans = () => api.get("/sitemap/plans").then(r => r.data);

// Fan-Out Query Intelligence
export const fanoutExpand = (seedTopic, mode = "simple", save = true) =>
  api.post("/fanout/expand", { seed_topic: seedTopic, mode, save }).then(r => r.data);
export const generateContentStrategy = (industry, solution, persona, mode = "complex") =>
  api.post("/fanout/strategy", { industry, solution, persona, mode }).then(r => r.data);
export const fanoutExpandAndClassify = (seedTopic, mode = "simple", save = true) =>
  api.post("/fanout/expand-and-classify", { seed_topic: seedTopic, mode, save }).then(r => r.data);
export const fanoutDetectIntent = (query) =>
  api.post("/fanout/detect-intent", { query }).then(r => r.data);
export const getFanoutQueries = (seedTopic, limit) =>
  api.get("/fanout/queries", { params: { ...(seedTopic ? { seed_topic: seedTopic } : {}), ...(limit ? { limit } : {}) } }).then(r => r.data);
export const getFanoutSeeds = () => api.get("/fanout/seeds").then(r => r.data);
export const approveGapQuery = (queryId) =>
  api.post(`/fanout/queries/${queryId}/approve`).then(r => r.data);
export const getGapQueryApprovalStatus = (queryId) =>
  api.get(`/fanout/queries/${queryId}/approval-status`).then(r => r.data);
export const deleteFanoutQueries = (seedTopic) =>
  api.delete("/fanout/queries", { params: { seed_topic: seedTopic } }).then(r => r.data);

// Technical Audit
export const startAudit = () => api.post("/audit/run").then(r => r.data);
export const getAuditStatus = () => api.get("/audit/status").then(r => r.data);
export const getAuditIssues = (params = {}) => api.get("/audit/issues", { params }).then(r => r.data);
export const getAuditSummary = () => api.get("/audit/summary").then(r => r.data);
export const getAuditPageDetail = (pageId) => api.get(`/audit/page/${pageId}`).then(r => r.data);
export const resolveAuditIssue = (issueId) => api.post(`/audit/resolve/${issueId}`).then(r => r.data);
export const rewriteAuditIssue = (issueId) => api.post(`/audit/rewrite/${issueId}`).then(r => r.data);
export const applyAuditFix = (issueId, value) => api.post(`/audit/apply/${issueId}`, { value }).then(r => r.data);
export const triggerAuditEnrich = () => api.post("/audit/enrich").then(r => r.data);
export const getPsiData = (url, strategy = "mobile", refresh = false) =>
  api.get("/audit/psi", { params: { url, strategy, refresh } }).then(r => r.data);
export const getPsiHistory = (url, strategy = "mobile", limit = 30) =>
  api.get("/audit/psi/history", { params: { url, strategy, limit } }).then(r => r.data);

// Schema Intelligence
export const runSchemaAudit = () => api.post("/schema/run").then(r => r.data);
export const getSchemaStatus = () => api.get("/schema/status").then(r => r.data);
export const getSchemaSummary = () => api.get("/schema/summary").then(r => r.data);
export const getSchemaResults = (params = {}) => api.get("/schema/results", { params }).then(r => r.data);
export const getSchemaUrlDetail = (url) => api.get("/schema/url", { params: { url } }).then(r => r.data);
export const enrichSchemaWithAI = () => api.post("/schema/enrich").then(r => r.data);
export const runCompetitorAnalysis = (data) => api.post("/schema/compete", data).then(r => r.data);
export const getCompeteStatus = () => api.get("/schema/compete/status").then(r => r.data);
export const getCompeteResults = () => api.get("/schema/compete/results").then(r => r.data);
export const getCompeteDetail = (id) => api.get(`/schema/compete/${id}`).then(r => r.data);

// GSC Sitemaps
export const getGscSitemaps = () => api.get("/gsc/sitemaps").then(r => r.data);
export const submitGscSitemap = (feedpath) => api.post("/gsc/sitemaps", { feedpath }).then(r => r.data);

// GSC Search Performance
export const getGscSiteTotal = () => api.get("/gsc/site-total").then(r => r.data);
export const getPerformancePages = (params = {}) => api.get("/gsc/performance/pages", { params }).then(r => r.data);
export const getPerformanceQueries = (params = {}) => api.get("/gsc/performance/queries", { params }).then(r => r.data);
export const getOpportunitiesSummary = () => api.get("/gsc/performance/opportunities").then(r => r.data);
export const getActionQueue = (params = {}) => api.get("/gsc/performance/action-queue", { params }).then(r => r.data);
export const getContentGapMap = (params = {}) => api.get("/gsc/performance/content-gap-map", { params }).then(r => r.data);
export const getQueryTopics = (params = {}) => api.get("/gsc/performance/query-topics", { params }).then(r => r.data);
export const getClusterAuthority = (params = {}) => api.get("/gsc/performance/cluster-authority", { params }).then(r => r.data);
export const backfillClusterAuthority = () => api.post("/gsc/performance/cluster-authority/backfill").then(r => r.data);
export const getAudience = (params = {}) => api.get("/gsc/audience", { params }).then(r => r.data);
export const buildAudienceSegments = () => api.post("/gsc/audience/segments").then(r => r.data);
export const groupGscQueries = (queryIds) =>
  api.post("/gsc/performance/queries/group", { query_ids: queryIds }).then(r => r.data);
export const groupGscQueriesToRecommendations = (groups) =>
  api.post("/gsc/performance/queries/group-to-recommendations", { groups }).then(r => r.data);
export const getGscTrends = (params = {}) =>
  api.get("/gsc/trends", { params }).then(r => r.data);
export const pushRisingToRecommendations = (params = {}) =>
  api.post("/gsc/trends/push-rising-to-recommendations", null, { params }).then(r => r.data);
export const getGscPins = (params = {}) => api.get("/gsc/pins", { params }).then(r => r.data);
export const addGscPin = (kind, value) => api.post("/gsc/pins", { kind, value }).then(r => r.data);
export const removeGscPin = (kind, value) =>
  api.delete("/gsc/pins", { params: { kind, value } }).then(r => r.data);

// GSC Query Gap Analysis
export const getQueryGaps = () => api.get("/gsc/query-gaps").then(r => r.data);
export const runQueryGapAnalysis = (daysBack = 28) =>
  api.post("/gsc/query-gaps/run", null, { params: { days_back: daysBack } }).then(r => r.data);
export const listGscQueries = (params = {}) => api.get("/gsc/queries", { params }).then(r => r.data);
export const getGscQuery = (queryId) => api.get(`/gsc/queries/${queryId}`).then(r => r.data);
export const analyzeQueryPage = (queryId) =>
  api.post(`/gsc/queries/${queryId}/analyze-page`).then(r => r.data);
export const autoOptimizeQuery = (queryId) =>
  api.post(`/gsc/queries/${queryId}/auto-optimize`).then(r => r.data);
export const getOptimizeStatus = (queryId) =>
  api.get(`/gsc/queries/${queryId}/optimize-status`).then(r => r.data);
export const suggestCtrFix = (queryId) =>
  api.post(`/gsc/queries/${queryId}/suggest-ctr-fix`).then(r => r.data);
// Rejects when any field's CMS write reports success:false (the endpoint itself still returns 200).
export const applyCtrFix = (queryId, { title, meta_desc }) =>
  api.post(`/gsc/queries/${queryId}/apply-ctr-fix`, { title, meta_desc }).then(r => {
    const failed = Object.entries(r.data.applied || {}).filter(([, res]) => !res?.success);
    if (failed.length) {
      const detail = failed.map(([field, res]) => `${field === "meta_desc" ? "Meta description" : "Title"}: ${res?.message || "failed"}`).join(" · ");
      throw Object.assign(new Error(detail), { response: { data: { detail } } });
    }
    return r.data;
  });
export const fanoutBridge = (topicA, topicB, focus = "") =>
  api.post("/fanout/bridge", { topic_a: topicA, topic_b: topicB, focus }).then(r => r.data);
export const fanoutToRecommendations = (seedTopic) =>
  api.post("/fanout/to-recommendations", { seed_topic: seedTopic }).then(r => r.data);

// Translation
export const translateArticle = (id, targetLanguage) =>
  api.post(`/articles/${id}/translate`, { target_language: targetLanguage }).then(r => r.data);
export const getArticleTranslations = (id) =>
  api.get(`/articles/${id}/translations`).then(r => r.data);
export const pushTranslation = (translationId) =>
  api.post(`/translations/${translationId}/push`).then(r => r.data);
export const getTranslationLanguages = () =>
  api.get("/translations/languages").then(r => r.data);

// Action Items
export const getActionItems = () => api.get("/action-items").then(r => r.data);
export const dismissActionItem = (key) => api.post(`/action-items/${key}/dismiss`).then(r => r.data);
export const restoreActionItem = (key) => api.post(`/action-items/${key}/restore`).then(r => r.data);
export const acceptActionItem = (key) => api.post(`/action-items/${key}/accept`).then(r => r.data);

// SEO Tasks — `key` is the human key (SEO-42) or the task uuid.
export const getTasks = () => api.get("/tasks").then(r => r.data);
export const getTask = (key) => api.get(`/tasks/${key}`).then(r => r.data);
export const getTaskMembers = () => api.get("/tasks/members").then(r => r.data);
export const createTask = (body) => api.post("/tasks", body).then(r => r.data);
export const updateTask = (key, patch) => api.patch(`/tasks/${key}`, patch).then(r => r.data);
export const deleteTask = (key) => api.delete(`/tasks/${key}`).then(r => r.data);
export const addTaskQueries = (key, queries) => api.post(`/tasks/${key}/queries`, { queries }).then(r => r.data);
export const removeTaskQueries = (key, queries) => api.post(`/tasks/${key}/queries/remove`, { queries }).then(r => r.data);
export const addTaskComment = (key, body) => api.post(`/tasks/${key}/comments`, { body }).then(r => r.data);
export const updateTaskComment = (key, id, body) => api.patch(`/tasks/${key}/comments/${id}`, { body }).then(r => r.data);
export const deleteTaskComment = (key, id) => api.delete(`/tasks/${key}/comments/${id}`).then(r => r.data);

// Reports (written by external agents via MCP `save_report`)
export const getReports = () => api.get("/reports").then(r => r.data);
export const getReport = (id) => api.get(`/reports/${id}`).then(r => r.data);
export const deleteReport = (id) => api.delete(`/reports/${id}`).then(r => r.data);
export const getReportTemplates = () => api.get("/report-templates").then(r => r.data);
export const createReportTemplate = (data) => api.post("/report-templates", data).then(r => r.data);
export const updateReportTemplate = (id, data) => api.put(`/report-templates/${id}`, data).then(r => r.data);
export const deleteReportTemplate = (id) => api.delete(`/report-templates/${id}`).then(r => r.data);

// AI citations (Google AI Overview snapshots). /run is synchronous and can take minutes.
export const getAiCitationQueries = () => api.get("/ai-citations/queries").then(r => r.data);
export const getAiCitationSuggestions = (params = {}) => api.get("/ai-citations/suggestions", { params }).then(r => r.data);
export const addAiCitationQueries = (data) => api.post("/ai-citations/queries", data).then(r => r.data);
export const deleteAiCitationQuery = (id) => api.delete(`/ai-citations/queries/${id}`).then(r => r.data);
export const runAiCitations = () => api.post("/ai-citations/run", {}, { timeout: 15 * 60 * 1000 }).then(r => r.data);
export const getAiCitationReport = (params = {}) => api.get("/ai-citations/report", { params }).then(r => r.data);
export const getAiCitationDiagnosis = (id, refresh = false) => api.get(`/ai-citations/queries/${id}/diagnosis`, { params: { refresh }, timeout: 90 * 1000 }).then(r => r.data);

// Primary target country of the active site (Settings → Business Context).
export const getTargetCountry = (detect = false) => api.get("/settings/target-country", { params: { detect } }).then(r => r.data);
export const setTargetCountry = (country) => api.put("/settings/target-country", { country }).then(r => r.data);
